import {
  Box, Grid, Stack, Typography, Card, CardActionArea, Radio, Divider, TextField,
  MenuItem, Chip, Paper, Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useDispatch, useSelector } from 'react-redux';
import {
  setRvpEnabled, setRvpMode, updateRvpFormula, updateRvpTcs,
} from '../../../../redux/pricingWizardSlice';
import SectionTitle from './SectionTitle';


const EXAMPLE_META = [
  { icon: '➕', color: '#dbeafe', text: '#1e40af', tag: 'flat_plus_fwd' },
  { icon: '✖️', color: '#dcfce7', text: '#166534', tag: 'pct_fwd' },
  { icon: '⚖️', color: '#fef9c3', text: '#854d0e', tag: 'higher_of' },
  { icon: '🔢', color: '#ede9fe', text: '#5b21b6', tag: 'flat_surcharge' },
];

function WorkedExamples() {
  const examples = [
    {
      t: 'RVP · Flat + FWD',
      d: 'Reverse = Flat ₹ + Forward rate (same zone / slab)',
      lines: [
        { label: 'Flat', value: '₹25' },
        { label: 'FWD Z3  0–500g', value: '₹27' },
        { label: 'RVP no-QC', value: '25 + 27 = ₹52' },
        { label: 'RVP with QC', value: '52 + 25 = ₹77' },
      ],
    },
    {
      t: 'RVP · % of FWD',
      d: 'Reverse = percentage of the forward rate',
      lines: [
        { label: 'RVP %', value: '150% of FWD' },
        { label: 'FWD Z3  0–500g', value: '₹27' },
        { label: 'RVP no-QC', value: '1.50 × 27 = ₹40.50' },
      ],
    },
    {
      t: 'RVP · Higher of',
      d: 'Reverse = MAX(Flat + FWD,  % × FWD)',
      lines: [
        { label: 'Flat', value: '₹25  |  RVP % = 150%' },
        { label: 'FWD Z3  0–500g', value: '₹27' },
        { label: 'Flat + FWD', value: '₹52' },
        { label: '% × FWD', value: '₹40.50' },
        { label: 'RVP', value: 'MAX(52, 40.50) = ₹52' },
      ],
    },
    {
      t: 'Flat surcharge on FWD',
      d: 'Fixed amount stacked on top of the forward rate',
      lines: [
        { label: 'FWD Z2  500–1000g', value: '₹42' },
        { label: 'Flat surcharge', value: '₹25' },
        { label: 'Total', value: '₹42 + ₹25 = ₹67' },
      ],
    },
  ];

  return (
    <Accordion variant="outlined" sx={{ mt: 1.5, '&:before': { display: 'none' }, borderRadius: '8px !important' }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2, minHeight: 44, '&.Mui-expanded': { minHeight: 44 } }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography sx={{ fontSize: 15 }}>📐</Typography>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>Worked Examples</Typography>
          <Chip label="RVP formulas · Flat + FWD" size="small" sx={{ height: 18, fontSize: 10, bgcolor: '#f1f5f9', color: 'text.secondary' }} />
        </Stack>
      </AccordionSummary>

      <AccordionDetails sx={{ px: 2, pb: 2 }}>
        <Grid container spacing={1.5}>
          {examples.map((ex, i) => {
            const m = EXAMPLE_META[i];
            return (
              <Grid item xs={12} sm={6} key={ex.t}>
                <Paper
                  variant="outlined"
                  sx={{ overflow: 'hidden', height: '100%', borderColor: 'divider' }}
                >
                  {/* Card header */}
                  <Box sx={{ px: 1.5, py: 1, bgcolor: m.color, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                      <Typography sx={{ fontSize: 14 }}>{m.icon}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: m.text, fontSize: 12.5 }}>{ex.t}</Typography>
                    </Stack>
                    <Typography variant="caption" sx={{ color: m.text, opacity: 0.8, mt: 0.25, display: 'block' }}>{ex.d}</Typography>
                  </Box>

                  {/* Calculation rows */}
                  <Box sx={{ px: 1.5, py: 1 }}>
                    {ex.lines.map((line, li) => (
                      <Stack
                        key={li}
                        direction="row"
                        justifyContent="space-between"
                        alignItems="baseline"
                        sx={{
                          py: 0.4,
                          borderBottom: li < ex.lines.length - 1 ? '1px dashed' : '2px solid',
                          borderColor: li < ex.lines.length - 1 ? 'divider' : m.text,
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 10.5, color: 'text.secondary' }}
                        >
                          {line.label}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            fontFamily: 'JetBrains Mono, monospace',
                            fontSize: 11,
                            fontWeight: li === ex.lines.length - 1 ? 800 : 600,
                            color: li === ex.lines.length - 1 ? m.text : 'text.primary',
                          }}
                        >
                          {line.value}
                        </Typography>
                      </Stack>
                    ))}
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
}

function RvpConfig() {
  const dispatch = useDispatch();
  const rvp = useSelector((s) => s.pricingWizard.rvp);
  const rateCardMode = useSelector((s) => s.pricingWizard.rateCardMode);
  const f = rvp.formulas.__blended__ || { type: 'higher', flat: 0, mult: 1, qc: 0 };
  const flatNum = parseFloat(f.flat) || 0;
  const multNum = parseFloat(f.mult) || 0;
  const qcNum = parseFloat(f.qc) || 0;
  // RVP nQC is calculated on the FORWARD price per the selected type; QC = nQC + QC charges.
  // The card has no row price, so preview against a sample forward of ₹50.
  const SAMPLE_FWD = 50;
  const noqc = f.type === 'mult' ? SAMPLE_FWD * multNum
    : (f.type === 'add' || f.type === 'flat') ? SAMPLE_FWD + flatNum
    : Math.max(SAMPLE_FWD + flatNum, SAMPLE_FWD * multNum);
  const qcVal = noqc + qcNum;
  const noqcExpr = f.type === 'mult' ? 'FWD × M'
    : (f.type === 'add' || f.type === 'flat') ? 'FWD + ₹X'
    : 'higher of (FWD + ₹X) and (FWD × M)';

  return (
    <Box sx={{ mt: 2.5 }}>
      <Divider sx={{ mb: 1.5 }} />
      <SectionTitle icon="🔄" title="RVP Configuration" badge={rvp.enabled ? `MODE ${rvp.mode === 'forward_linked' ? 'A' : 'B'}` : 'disabled'} />
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5, mb: 1.25 }}>
        Choose whether Reverse Pickup is enabled, and if so whether RVP rates are <b>derived from forward via a formula</b> (Mode A)
        or <b>a standalone rate card</b> (Mode B).
      </Typography>

      <Grid container spacing={1.5}>
        {[{ v: true, t: 'Yes — RVP is part of this merchant', s: 'Pick the mode below.' }, { v: false, t: 'No — Forward only', s: 'Reverse pickup is not contracted.' }].map((o) => (
          <Grid item xs={12} md={6} key={String(o.v)}>
            <Card variant="outlined" sx={{ borderColor: rvp.enabled === o.v ? 'primary.main' : 'divider', bgcolor: rvp.enabled === o.v ? 'primary.lighter' : 'background.paper' }}>
              <CardActionArea onClick={() => dispatch(setRvpEnabled(o.v))} sx={{ p: 1.25 }}>
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <Radio checked={rvp.enabled === o.v} sx={{ p: 0.25, pointerEvents: 'none' }} />
                  <Box><Typography variant="body2" sx={{ fontWeight: 700 }}>{o.t}</Typography><Typography variant="caption" sx={{ color: 'text.secondary' }}>{o.s}</Typography></Box>
                </Stack>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>

      {rvp.enabled && (
        <>
          <Grid container spacing={1.5} sx={{ mt: 0.25 }}>
            {[{ id: 'forward_linked', t: 'MODE A · Forward-linked', s: 'Reverse = formula(forward). No separate matrix.' }, { id: 'independent', t: 'MODE B · Independent', s: 'Full standalone reverse rate card (Stage 4 RVP tab).' }].map((o) => (
              <Grid item xs={12} md={6} key={o.id}>
                <Card variant="outlined" sx={{ borderColor: rvp.mode === o.id ? 'primary.main' : 'divider', bgcolor: rvp.mode === o.id ? 'primary.lighter' : 'background.paper' }}>
                  <CardActionArea onClick={() => dispatch(setRvpMode(o.id))} sx={{ p: 1.25 }}>
                    <Stack direction="row" spacing={1} alignItems="flex-start">
                      <Radio checked={rvp.mode === o.id} sx={{ p: 0.25, pointerEvents: 'none' }} />
                      <Box><Typography variant="body2" sx={{ fontWeight: 700 }}>{o.t}</Typography><Typography variant="caption" sx={{ color: 'text.secondary' }}>{o.s}</Typography></Box>
                    </Stack>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>

          {rvp.mode === 'forward_linked' && (
            <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 1.5, bgcolor: 'background.neutral', border: 1, borderColor: 'divider' }}>
              <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: 0.5 }}>
                Mode A formula · {rateCardMode === 'blended' ? 'one for all reverse couriers' : 'per reverse courier (Stage 4)'}
              </Typography>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mt: 1 }}>
                <TextField size="small" select label="Formula type" value={f.type || 'higher'} sx={{ minWidth: 220 }}
                  onChange={(e) => dispatch(updateRvpFormula({ parent: '__blended__', patch: { type: e.target.value } }))}>
                  <MenuItem value="add">FWD + ₹X</MenuItem>
                  <MenuItem value="mult">FWD × M</MenuItem>
                  <MenuItem value="higher">Higher of (FWD + ₹X, FWD × M)</MenuItem>
                </TextField>
                <TextField size="small" label="Flat (₹X)" value={f.flat} sx={{ minWidth: 130 }}
                  inputProps={{ inputMode: 'decimal' }}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === '' || v === '-' || /^-?\d*\.?\d*$/.test(v))
                      dispatch(updateRvpFormula({ parent: '__blended__', patch: { flat: v === '' || v === '-' ? v : Number(v) } }));
                  }} />
                <TextField size="small" label="Multiplier (M)" value={f.mult} sx={{ minWidth: 130 }}
                  inputProps={{ inputMode: 'decimal' }}
                  error={Number(f.mult) === 0 || f.mult === ''}
                  helperText={Number(f.mult) === 0 || f.mult === '' ? 'Must be > 0' : ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === '' || /^\d*\.?\d*$/.test(v))
                      dispatch(updateRvpFormula({ parent: '__blended__', patch: { mult: v.endsWith('.') ? v : (v === '' ? v : Number(v) || v) } }));
                  }} />
                <TextField size="small" label="QC charges (₹)" value={f.qc ?? ''} sx={{ minWidth: 130 }}
                  inputProps={{ inputMode: 'decimal' }}
                  helperText="Added to nQC for RVP QC"
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === '' || /^\d*\.?\d*$/.test(v))
                      dispatch(updateRvpFormula({ parent: '__blended__', patch: { qc: v.endsWith('.') ? v : (v === '' ? v : Number(v) || 0) } }));
                  }} />
              </Stack>
              <Typography variant="caption" sx={{ color: 'primary.dark', display: 'block', mt: 1 }}>
                Preview: at Forward = ₹{SAMPLE_FWD} → RVP nQC = ₹{noqc} · RVP QC = ₹{qcVal} &nbsp;(nQC = {noqcExpr}; QC = nQC + QC charges; varies per courier price)
              </Typography>
            </Box>
          )}

          {rvp.mode === 'independent' && (
            <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 1.5, bgcolor: 'background.neutral', border: 1, borderColor: 'divider' }}>
              <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: 0.5 }}>
                Mode B · Independent RVP
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
                The rate-card workbook (Stage 4) carries an <b>RVP sheet</b> — fill the without-QC price per reverse
                courier × zone × slab. The QC charge below is <b>mandatory</b>: with-QC = without-QC + QC charge.
              </Typography>
              <TextField
                size="small"
                label="QC charge (₹) — required"
                value={rvp.tcs.qc_amount ?? ''}
                sx={{ minWidth: 220 }}
                inputProps={{ inputMode: 'decimal' }}
                error={rvp.tcs.qc_amount === '' || rvp.tcs.qc_amount == null}
                helperText={(rvp.tcs.qc_amount === '' || rvp.tcs.qc_amount == null)
                  ? 'Enter the QC charge (0 is allowed when QC is free).'
                  : 'Applied on upload: RVP With QC = sheet value + this.'}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === '' || /^\d*\.?\d*$/.test(v)) {
                    dispatch(updateRvpTcs({
                      qc_amount: v === '' ? '' : (v.endsWith('.') ? v : Number(v)),
                      qc_applicable: v !== '' && Number(v) > 0,
                    }));
                  }
                }}
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
}

// "Set up commercial construct" (per-service arrangement cards) was removed per the
// Rate Card Modification MoM — the workbook grid is inherently zone × weight-slab, and
// nothing downstream read the arrangement type. The couriers-mapped / rate-card-blocked
// warning moved to the Profile & Service step. RVP configuration stays here.
export default function ArrangementBlock() {
  return (
    <Box sx={{ mt: 2.5 }}>
      <Divider sx={{ mb: 1.5 }} />
      <WorkedExamples />
      <RvpConfig />
    </Box>
  );
}
